import { useEffect, useState } from 'react';

export type CSSVariableNames =
    | '--color-brand-primary'
    | '--color-brand-primary-light'
    | '--color-brand-primary-dark'
    | '--color-brand-primary-darker'
    | '--color-brand-secondary'
    | '--color-brand-secondary-light'
    | '--color-brand-secondary-dark'
    | '--color-brand-secondary-darker'
    | '--color-brand-accent'
    | '--color-brand-accent-light'
    | '--color-brand-accent-lighter'
    | '--color-brand-accent-dark'
    | '--color-brand-accent-darker'
    | '--color-brand-green'
    | '--color-brand-green-light'
    | '--color-brand-green-dark'
    | '--color-brand-green-darker'
    | '--color-brand-red'
    | '--color-brand-red-light'
    | '--color-brand-red-dark'
    | '--color-brand-red-darker'
    | '--color-brand-button-text'
    | '--color-brand-button-background'
    | '--color-brand-background'
    | '--color-brand-background-light'
    | '--color-brand-background-dark'
    | '--color-brand-border'
    | '--color-brand-border-neutral'
    | '--color-module-title'
    | '--color-brand-neutral'
    | '--color-brand-neutral-light'
    | '--color-brand-neutral-dark'
    | '--color-brand-neutral-darker'
    | '--color-brand-neutral-darker-2'
    | '--color-brand-neutral-darkest'
    | '--color-brand-neutral-50'
    | '--color-brand-neutral-100'
    | '--color-brand-neutral-200'
    | '--color-brand-neutral-300'
    | '--color-brand-neutral-400'
    | '--color-brand-neutral-500'
    | '--color-brand-neutral-600'
    | '--color-brand-neutral-700'
    | '--color-brand-neutral-800'
    | '--color-brand-neutral-900'
    | '--color-brand-neutral-950'
    | '--color-brand-shadow'
    | '--color-brand-scrollbar'
    | '--color-brand-scrollbar-dark'
    | '--color-brand-scrollbar-darker'
    | '--color-brand-chart-neutral'
    | '--color-brand-chart-neutral-darker'
    | '--color-brand-chart-neutral-darkest'
    | '--color-brand-chart-background'
    | '--color-brand-chart-platform'
    | '--color-brand-chart-button'
    | '--color-brand-chart-button-hover'
    | '--color-brand-chart-text'
    | '--color-brand-chart-text-active'
    | '--font-sans'
    | '--font-secondary'
    | '--mosaic-brand-border-margin'
    | '--mosaic-brand-border-margin-negative'
    | '--mosaic-brand-toolbar-color'
    | '--mosaic-brand-toolbar-hover'
    | '--mosaic-brand-preview-box-shadow'
    | '--mosaic-brand-preview-border-radius';

export type CSSVariables = {
    [key in CSSVariableNames]: string;
};
// https://tailwindcss.com/docs/customizing-colors

const usePopOutCSSVariables = () => {
    const [variables, setVariables] = useState<CSSVariables>({} as CSSVariables);

    // create an array of variable names
    const variableNames: CSSVariableNames[] = [
        '--color-brand-primary',
        '--color-brand-primary-light',
        '--color-brand-primary-dark',
        '--color-brand-primary-darker',
        '--color-brand-secondary',
        '--color-brand-secondary-light',
        '--color-brand-secondary-dark',
        '--color-brand-secondary-darker',
        '--color-brand-accent',
        '--color-brand-accent-light',
        '--color-brand-accent-lighter',
        '--color-brand-accent-dark',
        '--color-brand-accent-darker',
        '--color-brand-green',
        '--color-brand-green-light',
        '--color-brand-green-dark',
        '--color-brand-green-darker',
        '--color-brand-red',
        '--color-brand-red-light',
        '--color-brand-red-dark',
        '--color-brand-red-darker',
        '--color-brand-button-text',
        '--color-brand-button-background',
        '--color-brand-background',
        '--color-brand-background-light',
        '--color-brand-background-dark',
        '--color-brand-border',
        '--color-brand-border-neutral',
        '--color-module-title',
        '--color-brand-neutral',
        '--color-brand-neutral-light',
        '--color-brand-neutral-dark',
        '--color-brand-neutral-darker',
        '--color-brand-neutral-darker-2',
        '--color-brand-neutral-darkest',
        '--color-brand-neutral-50',
        '--color-brand-neutral-100',
        '--color-brand-neutral-200',
        '--color-brand-neutral-300',
        '--color-brand-neutral-400',
        '--color-brand-neutral-500',
        '--color-brand-neutral-600',
        '--color-brand-neutral-700',
        '--color-brand-neutral-800',
        '--color-brand-neutral-900',
        '--color-brand-neutral-950',
        '--color-brand-shadow',
        '--color-brand-scrollbar',
        '--color-brand-scrollbar-dark',
        '--color-brand-scrollbar-darker',
        '--color-brand-chart-neutral',
        '--color-brand-chart-neutral-darker',
        '--color-brand-chart-neutral-darkest',
        '--color-brand-chart-background',
        '--color-brand-chart-platform',
        '--color-brand-chart-button',
        '--color-brand-chart-button-hover',
        '--color-brand-chart-text',
        '--color-brand-chart-text-active',
        '--font-sans',
        '--font-secondary',
        '--mosaic-brand-border-margin',
        '--mosaic-brand-border-margin-negative',
        '--mosaic-brand-toolbar-color',
        '--mosaic-brand-toolbar-hover',
        '--mosaic-brand-preview-box-shadow',
        '--mosaic-brand-preview-border-radius'
    ];

    // create an empty object to store the variable values

    useEffect(() => {
        let raf: number;

        const read = () => {
            const computedStyle = getComputedStyle(document.documentElement);
            const bg = computedStyle.getPropertyValue('--color-brand-background').trim();

            // Gate on a known-required variable
            if (!bg) {
                raf = requestAnimationFrame(read);
                return;
            }

            const next = {} as CSSVariables;

            variableNames.forEach((name) => {
                next[name] = computedStyle.getPropertyValue(name).trim();
            });

            setVariables(next); // 🔥 THIS triggers re-render
        };

        read();
        return () => cancelAnimationFrame(raf);
    }, []);

    return variables || {};
};

export default usePopOutCSSVariables;
